import type { PropertyTypes } from '@aloft/toolkit-shared';
import { isDefined } from '@aloft/toolkit-shared';

/**
 * Based on the create order workflow requirements, the UAD Report must have the following:
 * - A REPORT element
 * - A SUBJECT_PROPERTY element
 * - A matching form type with the given propertyType
 * - At least one COMPARABLE_SALE element
 *
 * @param doc the xml document uploaded by the user -> parsed into a Document object
 * @returns a list of errors found in the UAD Report
 */
export const validateUADReportXMLV2 = (doc: Document, propertyType?: PropertyTypes): string[] => {
  const errors = [];

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    errors.push('Invalid XML uploaded');

    // Exit early if the XML is invalid
    return errors;
  }

  const report = doc.querySelector('VALUATION_RESPONSE REPORT');
  if (!report) errors.push('No REPORT found - must have a report present in the UAD Report');

  if (isDefined(propertyType) && !isValidFormTypeByPropertyType(doc, propertyType)) {
    errors.push('Invalid Report Type - the report type must match the order property type');
  }

  const subject = doc.querySelector('PROPERTY');
  if (!subject) {
    errors.push(
      'No SUBJECT_PROPERTY found - must have a subject property present in the UAD Report',
    );
  }

  const comps = doc.querySelectorAll('COMPARABLE_SALE');
  if (comps.length < 1) {
    errors.push(
      'No COMPARABLE_SALE found - must have at least one comparable sale present in the UAD Report',
    );
  }

  return errors;
};

const isValidFormTypeByPropertyType = (doc: Document, propertyType: PropertyTypes): boolean => {
  const validSFRForms = ['FNM1004', 'FNM2055'];
  const validManufacturedForms = ['FNM1004C'];
  const validMultiFamilyForms = ['FNM1025'];
  const validCondoForms = ['FNM1073', 'FNM1075'];

  const report = doc.getElementsByTagName('REPORT');

  const formType = report[0]?.getAttribute('AppraisalFormType');

  if (!formType) return false;

  switch (propertyType) {
    case 'singleFamilyResidence':
      return validSFRForms.includes(formType);
    case 'manufacturedHome':
      return validManufacturedForms.includes(formType);
    case 'multiFamily':
      return validMultiFamilyForms.includes(formType);
    case 'condominium':
      return validCondoForms.includes(formType);
    default:
      return false;
  }
};
